import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDraggable } from './hooks/useDraggable';
import {
  ChatContainer,
  ChatToggleButton,
  ChatWindow,
  ChatHeader,
  ChatTitle,
  ChatSubtitle,
  CloseButton,
  ChatBody,
  Message,
  MessageBubble,
  LoadingMessage,
  LoadingBubble,
  LoadingSpinner,
  LoadingText,
  ChatInput,
  InputField,
  SendButton
} from './styled';

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ApiResponse {
  finished: boolean;
  data?: any;
}

interface ChatWidgetProps {
  token: string;
  env: string;
  envSuffix: string;
  chatNodeRequest: (data: any, token: string) => Promise<any>;
  chatNodePull: (id: string, includeData: boolean) => Promise<any>;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({
  token,
  env,
  envSuffix,
  chatNodeRequest,
  chatNodePull
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).substring(2, 15));
  
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Use the draggable hook
  const {
    wasDragged,
    containerRef,
    dragHandlers,
    getContainerStyle,
    getDragElementStyle,
    isNearLeftEdge
  } = useDraggable({
    elementWidth: 48, // ChatToggleButton width
    padding: 20, // Distance from edges
    dragThreshold: 5, // Minimum pixels to move before considering it a drag
    persistPosition: true // Save position to localStorage
  });

  // Initial messages
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const initialMessages: ChatMessage[] = [
        {
          id: '1',
          text: 'Hi there! 👋',
          isUser: false,
          timestamp: new Date()
        },
        {
          id: '2',
          text: 'My name is Aria. How can I assist you today?',
          isUser: false,
          timestamp: new Date()
        }
      ];
      setMessages(initialMessages);
    }
  }, [isOpen, messages.length]);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Auto-focus input when widget opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure the widget is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Auto-focus input when answer is received (when loading stops and new message is added)
  useEffect(() => {
    if (!isLoading && isOpen && inputRef.current && messages.length > 0) {
      // Check if the last message is from the bot (not user)
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage.isUser) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
  }, [isLoading, isOpen, messages]);

  const generateMessageId = () => Math.random().toString(36).substring(2, 15);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || !token) return;

    const userMessage: ChatMessage = {
      id: generateMessageId(),
      text: inputValue.trim(),
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Send POST request to webhook using chatNodeRequest
      const postResponse = await chatNodeRequest({
        chatInput: userMessage.text,
        sessionId: sessionId,
        env: env
      }, token);

      // console.log('POST response:', postResponse);

      if (postResponse.status !== 200) {
        throw new Error('Failed to send message');
      }

      // Get the execution ID from the response
      const postData = /* await  */postResponse.data;
      const executionId = postData.id;

      if (!executionId) {
        throw new Error('No execution ID received from webhook');
      }

      // Start polling for response with the execution ID
      await pollForResponse(executionId);

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: generateMessageId(),
        text: 'Sorry, I encountered an error. Please try again.',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  // Helper function for making authenticated requests to n8n API
  const fetchExecutionStatus = async (executionId: string, includeData: boolean = false): Promise<any> => {
    return chatNodePull(executionId, includeData);
  };

  const pollForResponse = async (executionId: string): Promise<void> => {
    const maxAttempts = 60; // 30 attempts * 2 seconds = 1 minute timeout
    let attempts = 0;

    const poll = async (): Promise<void> => {
      try {
        attempts++;
        
        const response = await fetchExecutionStatus(executionId, false);
        
        // Check HTTP status code
        if (response.status !== 200) {
          throw new Error('Failed to check execution status');
        }

        const data: ApiResponse = response.data;

        if (data.finished) {
          // When finished is true, make another request with includeData=true to get the actual data
          const finalResponse = await fetchExecutionStatus(executionId, true);
          
          if (finalResponse.status === 200) {
            // Extract the bot response from the data field
            let botResponseText = 'I received your message, but I\'m having trouble processing it right now.';
            
            // The data field is now available in the response when includeData=true
            try {
              const agentName = `Arootah Coach AI Agent ${envSuffix}`;
              if (finalResponse.data?.data?.resultData?.runData?.[agentName]?.[0]?.data?.main?.[0]?.[0]?.json?.output) {
                botResponseText = finalResponse.data.data.resultData.runData[agentName][0].data.main[0][0].json.output;

              }
            } catch (error) {
              console.error('Error extracting response:', error);
            }

            const botMessage: ChatMessage = {
              id: generateMessageId(),
              text: botResponseText,
              isUser: false,
              timestamp: new Date()
            };

            setMessages(prev => [...prev, botMessage]);
            setIsLoading(false);
          } else {
            throw new Error('Failed to get response data');
          }
        } else if (attempts < maxAttempts) {
          // Continue polling after 2 seconds
          setTimeout(poll, 2000);
        } else {
          // Timeout
          throw new Error('Response timeout');
        }
      } catch (error) {
        console.error('Error polling for response:', error);
        const errorMessage: ChatMessage = {
          id: generateMessageId(),
          text: 'Sorry, I took too long to respond. Please try again.',
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        setIsLoading(false);
      }
    };

    await poll();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeChat();
    }
  };

  const toggleChat = () => {
    // Don't toggle if the user was dragging
    if (wasDragged) {
      return;
    }
    
    if (isOpen) {
      closeChat();
    } else {
      setIsOpen(true);
      setIsClosing(false);
    }
  };

  const closeChat = () => {
    setIsClosing(true);
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 320); // Slightly longer than animation duration to prevent flicker
  };

  // Calculate chat window positioning based on button location
  const chatWindowStyle = isNearLeftEdge() ? {
    left: '0',
    right: 'auto'
  } : {
    right: '0',
    left: 'auto'
  };

  return (
    <ChatContainer
      ref={containerRef}
      style={getContainerStyle()}
    >
      <ChatToggleButton
        onClick={toggleChat}
        onMouseDown={dragHandlers.onMouseDown}
        onTouchStart={dragHandlers.onTouchStart}
        style={getDragElementStyle()}
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
          </svg>
        )}
      </ChatToggleButton>

      <ChatWindow
        isOpen={isOpen}
        isClosing={isClosing}
        style={chatWindowStyle}
      >
        <ChatHeader>
          <div>
            <ChatTitle>Aria</ChatTitle>
            <ChatSubtitle>How can I help?</ChatSubtitle>
          </div>
          <CloseButton onClick={closeChat}>
            <svg viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </CloseButton>
        </ChatHeader>

        <ChatBody ref={chatBodyRef}>
          {messages.map((message) => (
            <Message key={message.id} isUser={message.isUser}>
              <MessageBubble isUser={message.isUser}>
                {message.isUser ? (
                  message.text
                ) : (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({ children, ...props }) => (
                        <div className="table-wrapper">
                          <table {...props}>{children}</table>
                        </div>
                      ),
                    }}
                  >
                    {message.text}
                  </ReactMarkdown>
                )}
              </MessageBubble>
            </Message>
          ))}
          
          {isLoading && (
            <LoadingMessage>
              <LoadingBubble>
                <LoadingSpinner />
                <LoadingText>Aria is typing...</LoadingText>
              </LoadingBubble>
            </LoadingMessage>
          )}
        </ChatBody>

        <ChatInput>
          <InputField
            ref={inputRef}
            type="text"
            placeholder="Type your question..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <SendButton onClick={sendMessage} disabled={isLoading || !inputValue.trim()}>
            <svg viewBox="0 0 24 24">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </SendButton>
        </ChatInput>
      </ChatWindow>
    </ChatContainer>
  );
};
